import { privateRequest, type ApiResponse } from "../axios";

export interface UserLeaveBalanceDto {
  Id?: number;
  LeaveBalanceId?: number;
  UserId: number;
  EmployeeName?: string;
  EmployeeNo?: string;
  LeaveTypeId: number;
  LeaveTypeName?: string;
  LeaveType?: string;
  Year: number;
  Accrued?: number;
  AccruedBalance?: number;
  Consumed?: number;
  ConsumedBalance?: number;
  Reserved?: number;
  ReservedBalance?: number;
  CarriedForward?: number;
  CarriedForwardBalance?: number;
  Encashed?: number;
  EncashedBalance?: number;
  AvailableBalance?: number;
  Balance?: number;
}

export interface UserLeaveBalanceSummaryDto {
  UserId: number;
  EmployeeName?: string;
  EmployeeNo?: string;
  Year?: number;
  TotalLeave?: number;
  TotalAccrued?: number;
  TotalConsumed?: number;
  TotalReserved?: number;
  TotalCarriedForward?: number;
  TotalEncashed?: number;
  TotalAvailableBalance?: number;
  CarriedForward?: number;
  CarriedForwardBalance?: number;
  Encashed?: number;
  EncashedBalance?: number;
  AvailableBalance?: number;
  Balance?: number;
  LeaveTypes?: UserLeaveBalanceDto[];
  Leaves?: UserLeaveBalanceDto[];
  [key: string]: any;
}

export interface LeaveBalanceLedgerDto {
  Id?: number;
  TransactionType?: string;
  Amount: number;
  BalanceAfter?: number;
  LeaveRequestId?: number | null;
  Remarks?: string;
  CreatedDate?: string;
  CreateDate?: string;
  UserName?: string;
  LeaveTypeName?: string;
}

export interface ManualBalanceAdjustmentDto {
  UserId: number;
  LeaveTypeId: number;
  IsCredit: boolean;
  AdjustmentType?: "Credit" | "Debit" | string;
  Amount: number;
  Remarks: string;
}

export interface EmployeeLeaveGroup {
  UserId: number;
  UserName?: string;
  EmployeeName?: string;
  EmployeeNo?: string;
  Year: number;
  leaveItems: UserLeaveBalanceDto[];
  totalAccrued: number;
  totalAvailable: number;
}

export interface GetLeaveBalanceSummaryParams {
  year: number;
  from?: number;
  to?: number;
  searchText?: string;
}

export interface GetLedgerParams {
  targetUserId?: number | undefined;
  UserId?: number | undefined;
  leaveTypeId?: number | undefined;
  LeaveTypeId?: number | undefined;
  year?: number | undefined;
  Year?: number | undefined;
  LeaveBalanceId?: number | undefined;
  FromDate?: string | undefined;
  ToDate?: string | undefined;
  isMyLedger?: boolean | undefined;
}

const ENDPOINTS = {
  LEAVE_BALANCE: "api/LeaveBalance",
  LEAVE_BALANCE_MY_LEDGER: "api/LeaveBalance/MyLedger",
  LEAVE_BALANCE_SUMMARY: "api/LeaveBalance/Summary",
  LEAVE_BALANCE_USER: "api/LeaveBalance/LeaveDetail",
  LEAVE_BALANCE_LEDGER: "api/LeaveBalance/Ledger",
  LEAVE_BALANCE_ADJUST: "api/LeaveBalance/Adjust",
};

const getLeaveBalanceSummary = async (params: GetLeaveBalanceSummaryParams): Promise<ApiResponse<any>> => {
  const queryParams = new URLSearchParams();
  if (params.year) queryParams.append("year", params.year.toString());
  if (params.from !== undefined) queryParams.append("from", params.from.toString());
  if (params.to !== undefined) queryParams.append("to", params.to.toString());
  if (params.searchText) queryParams.append("searchText", params.searchText);

  const queryString = queryParams.toString();
  const url = queryString ? `${ENDPOINTS.LEAVE_BALANCE_SUMMARY}?${queryString}` : ENDPOINTS.LEAVE_BALANCE_SUMMARY;
  const res = await privateRequest.get<ApiResponse<any>>(url);
  return res.data;
};

const getMyLeaveBalance = async (year?: number): Promise<ApiResponse<UserLeaveBalanceDto[]>> => {
  const queryParams = new URLSearchParams();
  if (year) queryParams.append("year", year.toString());
  const queryString = queryParams.toString();
  const url = queryString ? `${ENDPOINTS.LEAVE_BALANCE}?${queryString}` : ENDPOINTS.LEAVE_BALANCE;
  const res = await privateRequest.get<ApiResponse<UserLeaveBalanceDto[]>>(url);
  return res.data;
};

const getUserLeaveBalance = async (
  userId?: number,
  year?: number
): Promise<ApiResponse<UserLeaveBalanceDto[]>> => {
  if (!userId) {
    return getMyLeaveBalance(year);
  }

  const params: { targetUserId: number; year?: number } = {
    targetUserId: userId,
  };

  if (year) {
    params.year = year;
  }

  const res = await privateRequest.get<ApiResponse<UserLeaveBalanceDto[]>>(
    `${ENDPOINTS.LEAVE_BALANCE_USER}`,
    { params }
  );

  return res.data;
};

const getMyLeaveLedger = async (params: { leaveTypeId?: number | undefined; year?: number | undefined }): Promise<ApiResponse<LeaveBalanceLedgerDto[]>> => {
  const queryParams = new URLSearchParams();
  if (params.leaveTypeId) queryParams.append("leaveTypeId", params.leaveTypeId.toString());
  if (params.year) queryParams.append("year", params.year.toString());

  const queryString = queryParams.toString();
  const url = queryString ? `${ENDPOINTS.LEAVE_BALANCE_MY_LEDGER}?${queryString}` : ENDPOINTS.LEAVE_BALANCE_MY_LEDGER;
  const res = await privateRequest.get<ApiResponse<LeaveBalanceLedgerDto[]>>(url);
  return res.data;
};

const getLeaveBalanceLedger = async (params: GetLedgerParams): Promise<ApiResponse<LeaveBalanceLedgerDto[]>> => {
  if (params?.isMyLedger || (!params?.targetUserId && !params?.UserId && !params?.LeaveBalanceId)) {
    const leaveTypeId = params?.leaveTypeId ?? params?.LeaveTypeId;
    const year = params?.year ?? params?.Year;
    return getMyLeaveLedger({ leaveTypeId, year });
  }

  const queryParams = new URLSearchParams();
  const targetUserId = params?.targetUserId ?? params?.UserId;
  const leaveTypeId = params?.leaveTypeId ?? params?.LeaveTypeId;
  const year = params?.year ?? params?.Year;

  if (targetUserId) queryParams.append("targetUserId", targetUserId.toString());
  if (leaveTypeId) queryParams.append("leaveTypeId", leaveTypeId.toString());
  if (year) queryParams.append("year", year.toString());
  if (params?.LeaveBalanceId) queryParams.append("LeaveBalanceId", params.LeaveBalanceId.toString());

  const queryString = queryParams.toString();
  const url = queryString ? `${ENDPOINTS.LEAVE_BALANCE_LEDGER}?${queryString}` : ENDPOINTS.LEAVE_BALANCE_LEDGER;
  const res = await privateRequest.get<ApiResponse<LeaveBalanceLedgerDto[]>>(url);
  return res.data;
};

const adjustLeaveBalance = async (payload: ManualBalanceAdjustmentDto): Promise<ApiResponse<any>> => {
  const res = await privateRequest.post<ApiResponse<any>>(ENDPOINTS.LEAVE_BALANCE_ADJUST, payload);
  return res.data;
};

const LeaveBalanceService = {
  getLeaveBalanceSummary,
  getUserLeaveBalance,
  getMyLeaveBalance,
  getLeaveBalanceLedger,
  getMyLeaveLedger,
  adjustLeaveBalance,
};

export default LeaveBalanceService;
